import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

import { type AuctionType, TrackingEvents, TrackingPayload } from '../core.types';

export enum AuctionCardClickedTrigger {
    BidNowClick = 'Bid Now Click',
    HouseNameClick = 'House Name Click',
    ImageClick = 'Image Click',
    TitleClick = 'Title Click',
}

const VALID_TRIGGERS = new Set<AuctionCardClickedTrigger>(Object.values(AuctionCardClickedTrigger));

export function isAuctionCardClickedTrigger(value: string): value is AuctionCardClickedTrigger {
    return VALID_TRIGGERS.has(value as AuctionCardClickedTrigger);
}

type TrackAuctionCardClickedPayload = TrackingPayload & {
    auctionType: AuctionType;
    catalogId: number;
    houseId: number;
    lotCount: number;
    position: number;
    trigger: AuctionCardClickedTrigger;
};

type TrackAuctionCardClickedCallbackParams = Omit<TrackAuctionCardClickedPayload, 'pageName' | 'url'>;

const trackAuctionCardClickedAnalytics = (payload: TrackAuctionCardClickedPayload) => {
    track(TrackingEvents.Catalog.AuctionCardClicked, payload);
};

export const useTrackAuctionCardClickedAnalytics = () => {
    const { pathname, search } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    return useCallback(
        (params: TrackAuctionCardClickedCallbackParams) => {
            const { auctionType, catalogId, houseId, lotCount, position, trigger } = params;
            const pageName = getAmplitudePageNameFromPath(pathname, search);

            trackAuctionCardClickedAnalytics({
                auctionType,
                catalogId,
                houseId,
                lotCount,
                pageName,
                position,
                trigger,
                url,
            });
        },
        [pathname, search, url]
    );
};
